import { useState } from 'react';
import type { DiagnosticAnswers, DiagnosticResult } from '@shared/prospect-diagnostic';
import { normalizeWebsiteDomain } from '@shared/company-signup';
import { DiagnosticAlert, DiagnosticLayout, StepNav, diagnosticPrimary } from '@/components/diagnostic/DiagnosticParts';
import { DiagnosticQuestion } from '@/components/diagnostic/DiagnosticQuestion';
import { DiagnosticResultView } from '@/components/diagnostic/DiagnosticResultView';
import { DiagnosticWriting } from '@/components/diagnostic/DiagnosticWriting';
import { type DiagnosticDraft, type DraftPatch, diagnosticSteps, emptyDraft, isStepComplete, toAnswers } from '@/lib/diagnostic-steps';
import { labLeadDiagnostic } from '@/lib/lead-diagnostic';

type Outcome =
  | { kind: 'writing'; answers: DiagnosticAnswers }
  | { kind: 'ready'; result: DiagnosticResult }
  | { kind: 'failed'; message: string };

const secondaryButton =
  'flex min-h-[52px] items-center rounded-full border-2 border-domino-sand bg-white px-7 text-base font-semibold text-domino-aubergine disabled:opacity-50';

/**
 * Sandbox of the online diagnostic (02/11/2026, Domino operators): the same questions, then the
 * diagnostic a prospect would receive, without the identity check and without saving anything.
 * Answers stay on the page, so one can be changed and the diagnostic written again.
 */
export const DiagnosticLabPage = () => {
  const [draft, setDraft] = useState<DiagnosticDraft>(emptyDraft);
  const [index, setIndex] = useState(0);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  const update = (patch: DraftPatch) =>
    setDraft((current) => ({ ...current, ...(typeof patch === 'function' ? patch(current) : patch) }));

  const steps = diagnosticSteps(draft);
  const step = steps[Math.min(index, steps.length - 1)] ?? 'website';
  const websiteValid = normalizeWebsiteDomain(draft.website) !== null;
  const answers = toAnswers(draft);
  const last = index >= steps.length - 1;

  const write = async (current: DiagnosticAnswers) => {
    setOutcome({ kind: 'writing', answers: current });
    const response = await labLeadDiagnostic(current);
    setOutcome(response.ok ? { kind: 'ready', result: response.result } : { kind: 'failed', message: response.message });
  };

  const next = () => {
    if (last && answers) void write(answers);
    else setIndex(index + 1);
  };

  const edit = () => {
    setOutcome(null);
    setIndex(0);
  };

  if (outcome?.kind === 'writing') {
    return (
      <DiagnosticLayout progress={null}>
        <DiagnosticWriting answers={outcome.answers} />
      </DiagnosticLayout>
    );
  }

  if (outcome) {
    return (
      <DiagnosticLayout progress={null}>
        {outcome.kind === 'ready' ? <DiagnosticResultView result={outcome.result} /> : <DiagnosticAlert>{outcome.message}</DiagnosticAlert>}
        <div className="flex flex-wrap gap-3">
          <button type="button" onClick={edit} className={diagnosticPrimary}>Modifier les réponses</button>
          {answers && <button type="button" onClick={() => void write(answers)} className={secondaryButton}>Réécrire</button>}
        </div>
      </DiagnosticLayout>
    );
  }

  return (
    <DiagnosticLayout progress={index / (steps.length + 1)}>
      <div key={step} className="flex flex-col gap-8">
        <DiagnosticQuestion step={step} draft={draft} onChange={update} onAnswered={() => undefined} />
        <StepNav
          onBack={index > 0 ? () => setIndex(index - 1) : null}
          onNext={next}
          nextLabel={last ? 'Découvrir le potentiel' : 'Continuer'}
          nextDisabled={!isStepComplete(step, draft, websiteValid)}
        />
      </div>
    </DiagnosticLayout>
  );
};
